import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/context/FavoritesContext";
import { ProductCard } from "@/components/ProductCard";
import { OrderCard } from "@/components/OrderCard";
import { ACCOUNT } from "@/constants/testIds";

const SignIn = ({ title }) => {
  const { login } = useAuth();
  return (
    <main className="pt-36 pb-10 max-w-xl mx-auto px-6 text-center min-h-[60vh]">
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold">{title}</h1>
      <p className="mt-4 text-stone-600">Sign in with Google to see your orders and saved favorites.</p>
      <button data-testid={ACCOUNT.loginButton} onClick={login} className="mt-8 px-7 py-3.5 rounded-full bg-[#1C1917] text-white hover:bg-[#E06D44] transition-colors">Sign in with Google</button>
      <p className="mt-6 text-sm text-stone-500">Checked out as a guest? <Link to="/track" className="underline">Track your order</Link></p>
    </main>
  );
};

export function AccountPage() {
  const { user, loading, logout } = useAuth();
  const [orders, setOrders] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    if (user) api.get("/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
    if (user) api.get("/admin/check").then(() => setIsAdmin(true)).catch(() => setIsAdmin(false));
  }, [user]);
  if (loading) return <main className="pt-32 min-h-[60vh]" />;
  if (!user) return <SignIn title="Your account" />;
  return (
    <main data-testid={ACCOUNT.page} className="pt-28 pb-10 max-w-4xl mx-auto px-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {user.picture && <img src={user.picture} alt="" className="w-14 h-14 rounded-full" />}
          <div>
            <h1 data-testid={ACCOUNT.name} className="font-heading text-3xl font-extrabold">Hi, {user.name.split(" ")[0]}</h1>
            <p className="text-sm text-stone-500">{user.email}</p>
          </div>
        </div>
        <div className="flex gap-3">
          {isAdmin && <Link to="/admin" data-testid="account-admin-link" className="px-5 py-2.5 rounded-full bg-[#E06D44] text-white text-sm hover:bg-[#C95B33] transition-colors">Shop admin</Link>}
          <Link to="/favorites" className="px-5 py-2.5 rounded-full border border-[#E7E0D3] text-sm hover:bg-[#F0EBE1] transition-colors">Favorites</Link>
          <button data-testid={ACCOUNT.logoutButton} onClick={logout} className="px-5 py-2.5 rounded-full border border-[#1C1917] text-sm hover:bg-[#1C1917] hover:text-white transition-colors">Sign out</button>
        </div>
      </div>
      <h2 className="font-heading mt-12 text-base md:text-lg font-semibold uppercase tracking-[0.2em] text-[#4E7B68]">My orders</h2>
      {orders && orders.length === 0 && <p data-testid={ACCOUNT.noOrders} className="mt-6 text-stone-500">No orders yet. <Link to="/shop" className="underline">Find something Kiddee loves</Link>.</p>}
      <div data-testid={ACCOUNT.orders} className="mt-6 space-y-4">
        {(orders || []).map((o) => <OrderCard key={o.order_id} order={o} />)}
      </div>
    </main>
  );
}

export function FavoritesPage() {
  const { user, loading } = useAuth();
  const { slugs } = useFavorites();
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (user) api.get("/favorites").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [user]);
  if (loading) return <main className="pt-32 min-h-[60vh]" />;
  if (!user) return <SignIn title="Your favorites" />;
  return (
    <main data-testid={ACCOUNT.favorites} className="pt-28 pb-10 max-w-7xl mx-auto px-6 lg:px-10">
      <h1 className="font-heading text-4xl font-extrabold tracking-tight">Saved items</h1>
      {items && !items.some((p) => slugs.includes(p.slug)) && <p className="mt-6 text-stone-500">Tap the heart on any product to save it here.</p>}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
        {(items || []).filter((p) => slugs.includes(p.slug)).map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
      </div>
    </main>
  );
}
